import React, { memo, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BellIcon,
  CalendarDaysIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  CubeIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  HomeIcon,
  ShoppingCartIcon,
  TruckIcon,
  UserGroupIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AppAssistant from './AppAssistant';
import { formatDateFR } from '../utils/date';
import {
  getAllowedStoreScopeOptions,
  readStoreScope,
  writeStoreScope,
} from '../utils/storeScope';

const BICICLO_LOGO_SRC = '/branding/biciclo-logo.png?v=20260605-2';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Clients', href: '/clients', icon: UserGroupIcon },
  { name: 'RDV', href: '/appointments', icon: CalendarDaysIcon },
  { name: 'Réparations', href: '/repairs', icon: WrenchScrewdriverIcon },
  { name: 'Devis', href: '/quotes', icon: DocumentTextIcon },
  { name: 'Commande Client', href: '/reservations', icon: CalendarDaysIcon },
  { name: 'Caisse', href: '/cashier', icon: ShoppingCartIcon },
  { name: 'Stocks', href: '/products', icon: CubeIcon },
  { name: 'Shopping', href: '/shopping', icon: ShoppingCartIcon },
  { name: 'Commande fournisseur', href: '/purchases', icon: TruckIcon },
  { name: 'Reporting', href: '/finance', icon: CurrencyDollarIcon },
  { name: 'Général', href: '/general', icon: Cog6ToothIcon },
];

function isPlatformOnlySession(user) {
  return Boolean((user?.is_staff || user?.is_superuser) && !user?.account_code && !user?.current_account);
}

function getNavigation(user) {
  if (isPlatformOnlySession(user)) {
    return [{ name: 'Dashboard', href: '/accounts', icon: BuildingOffice2Icon }];
  }

  const baseItems = navigation.filter((item) => item.href !== '/finance');
  const items = [...baseItems];

  if (user?.can_view_analytics) {
    items.splice(items.length - 2, 0, { name: 'Reporting', href: '/finance', icon: CurrencyDollarIcon });
  }

  if (user?.can_manage_users) {
    items.splice(items.length - 2, 0, { name: 'Utilisateurs', href: '/users', icon: UserGroupIcon });
  }

  if (user?.is_staff || user?.is_superuser) {
    items.splice(items.length - 2, 0, { name: 'Comptes', href: '/accounts', icon: BuildingOffice2Icon });
  }

  return items;
}

const defaultNotifications = [
  { id: 1, message: 'Stock faible sur plusieurs références atelier' },
  { id: 2, message: 'Des réparations terminées attendent un encaissement' },
];

function SidebarContent({ location, onNavigate, user, handleLogout, selectedStoreScope }) {
  const navigationItems = getNavigation(user);
  const isPlatformSession = isPlatformOnlySession(user);
  const availableStoreScopeOptions = getAllowedStoreScopeOptions(user);
  const selectedStoreLabel =
    isPlatformSession
      ? 'Plateforme'
      : availableStoreScopeOptions.find((option) => option.value === selectedStoreScope)?.label || 'Tout';
  const hasSpecificStore = selectedStoreScope !== 'all';

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[30px] border border-white/60 bg-white/72 p-3 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-2xl sm:p-4">
      <div className="relative shrink-0 overflow-hidden rounded-[24px] border border-slate-200/80 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 px-3 py-3 text-white shadow-[0_20px_50px_rgba(15,23,42,0.18)] sm:px-4 sm:py-4">
        <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-emerald-400/20 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-10 left-6 h-20 w-20 rounded-full bg-cyan-300/10 blur-2xl" />
        <div className="pointer-events-none absolute bottom-[-3.5rem] right-[-2.5rem] h-32 w-32 rounded-full border border-white/8" />
        <span className="absolute right-5 top-5 h-2.5 w-2.5 rounded-full bg-emerald-300 shadow-[0_0_18px_rgba(110,231,183,0.95)]" />

        <div className="relative flex min-w-0 flex-col gap-3">
          <div className="max-w-full pr-3 sm:pr-5">
            <div className="w-[clamp(8.5rem,68%,12rem)] max-w-full min-w-0 rounded-[20px] border border-emerald-300/20 bg-slate-950/88 p-2.5 shadow-[0_14px_30px_rgba(15,23,42,0.22)]">
              <div className="aspect-[1067/465] w-full">
                <img
                  src={BICICLO_LOGO_SRC}
                  alt="Logo Biciclo ERP"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-emerald-100/70 sm:text-[11px]">
              Atelier ERP
            </p>
            <h1 className="mt-2 truncate text-[1.35rem] font-semibold leading-none tracking-tight text-white sm:text-[1.65rem] xl:text-[1.85rem]">
              Biciclo
            </h1>
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2 text-[11px]">
            <span
              className={`inline-flex max-w-full items-center rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] ${
                hasSpecificStore
                  ? 'border-white/10 bg-white/14 text-white shadow-[0_10px_24px_rgba(15,23,42,0.18)] backdrop-blur'
                  : 'border-white/10 bg-white/8 text-slate-200'
              }`}
            >
              <span className="truncate">{selectedStoreLabel}</span>
            </span>
            <span className="min-w-0 text-emerald-100/70">
              {isPlatformSession ? 'Administration globale' : 'Interface atelier et boutique'}
            </span>
          </div>
        </div>
      </div>

      <nav className="mt-4 min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-1">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              className={`group flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-medium transition duration-200 ${
                isActive
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'text-slate-600 hover:bg-slate-100/90 hover:text-slate-900'
              }`}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-700'}`} />
              <span className="truncate">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="mt-3 shrink-0 rounded-[22px] border border-slate-200/80 bg-slate-50/90 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-700">
            <UserCircleIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">
              {user?.first_name || user?.last_name
                ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
                : user?.username || 'Utilisateur'}
            </p>
            <p className="truncate text-xs text-slate-500">{user?.email || 'Compte connecté'}</p>
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4" />
          Déconnexion
        </button>
      </div>
    </div>
  );
}

export default memo(function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const isPlatformSession = isPlatformOnlySession(user);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showDesktopSidebar, setShowDesktopSidebar] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1440 : true
  );
  const [showNotifications, setShowNotifications] = useState(false);
  const [isForegroundModalOpen, setIsForegroundModalOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [selectedStoreScope, setSelectedStoreScope] = useState(() => readStoreScope());
  const navigationItems = useMemo(() => getNavigation(user), [user]);
  const availableStoreScopeOptions = useMemo(() => getAllowedStoreScopeOptions(user), [user]);

  useEffect(() => {
    const syncViewportLayout = () => {
      const shouldPinSidebar = window.innerWidth >= 1440;
      setShowDesktopSidebar(shouldPinSidebar);

      if (shouldPinSidebar) {
        setSidebarOpen(false);
      }
    };

    syncViewportLayout();
    window.addEventListener('resize', syncViewportLayout);
    return () => window.removeEventListener('resize', syncViewportLayout);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const pageTitle = useMemo(() => {
    const current = navigationItems.find((item) => item.href === location.pathname);
    return current?.name || 'ERP';
  }, [location.pathname, navigationItems]);

  const formattedDate = useMemo(() => formatDateFR(now), [now]);

  useEffect(() => {
    const allowedValues = new Set(availableStoreScopeOptions.map((option) => option.value));
    if (!allowedValues.has(selectedStoreScope)) {
      writeStoreScope(availableStoreScopeOptions[0]?.value || 'all');
    }
  }, [availableStoreScopeOptions, selectedStoreScope]);

  useEffect(() => {
    const syncStoreScope = (event) => {
      if (event?.type === 'storage') {
        setSelectedStoreScope(readStoreScope());
        return;
      }

      if (event?.detail) {
        setSelectedStoreScope(event.detail);
        return;
      }

      setSelectedStoreScope(readStoreScope());
    };

    window.addEventListener('storage', syncStoreScope);
    window.addEventListener('erp-store-scope-change', syncStoreScope);
    return () => {
      window.removeEventListener('storage', syncStoreScope);
      window.removeEventListener('erp-store-scope-change', syncStoreScope);
    };
  }, []);

  useEffect(() => {
    const handleModalVisibility = (event) => {
      setIsForegroundModalOpen(Boolean(event?.detail?.active));
    };

    window.addEventListener('erp-foreground-modal-visibility', handleModalVisibility);
    return () => {
      window.removeEventListener('erp-foreground-modal-visibility', handleModalVisibility);
    };
  }, []);

  const handleLogout = () => {
    logout();
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('access');
    localStorage.removeItem('account_code');
    navigate('/login');
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-app-canvas text-slate-900">
      <div className="mx-auto flex min-h-screen w-full max-w-[1720px] items-start gap-3 px-3 py-3 sm:gap-4 sm:px-4 sm:py-4 lg:gap-5 lg:px-5 lg:py-5 xl:gap-6 xl:px-6 xl:py-6">
        {showDesktopSidebar && (
          <aside className="w-[clamp(16rem,20vw,19.5rem)] shrink-0 self-start xl:sticky xl:top-6 xl:h-[calc(100vh-3rem)] xl:max-h-[calc(100vh-3rem)]">
            <SidebarContent
              location={location}
              user={user}
              handleLogout={handleLogout}
              selectedStoreScope={selectedStoreScope}
            />
          </aside>
        )}

        {!showDesktopSidebar && sidebarOpen && (
          <div className="fixed inset-0 z-50 bg-slate-950/22">
            <div className="flex h-full">
              <div className="h-full w-[88%] max-w-[340px] p-2 sm:p-4">
                <SidebarContent
                  location={location}
                  user={user}
                  handleLogout={handleLogout}
                  onNavigate={() => setSidebarOpen(false)}
                  selectedStoreScope={selectedStoreScope}
                />
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="flex-1"
                aria-label="Fermer le menu"
              />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className={`glass-panel mb-4 rounded-[28px] px-4 py-3 transition duration-200 sm:px-5 lg:mb-6 lg:px-6 ${
            isForegroundModalOpen
              ? 'pointer-events-none opacity-15 blur-[2px] saturate-50'
              : 'opacity-100'
          }`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  onClick={() => setSidebarOpen((current) => !current)}
                  className={`${showDesktopSidebar ? 'hidden' : 'flex'} h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/80 text-slate-700 shadow-sm transition hover:bg-white`}
                >
                  {sidebarOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
                </button>

                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                    {formattedDate}
                  </p>
                  <h2 className="truncate text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                    {pageTitle}
                  </h2>
                </div>
              </div>

              <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:gap-3 md:w-auto md:justify-end">
                {!isPlatformSession && (
                <div className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-white/75 px-3 py-2 shadow-sm md:flex">
                  {availableStoreScopeOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => writeStoreScope(option.value)}
                      className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition ${
                        selectedStoreScope === option.value
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                )}

                <div className="relative">
                  <button
                    onClick={() => setShowNotifications((current) => !current)}
                    className="relative flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/80 text-slate-700 shadow-sm transition hover:bg-white"
                  >
                    <BellIcon className="h-5 w-5" />
                    {defaultNotifications.length > 0 && (
                      <span className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-rose-500" />
                    )}
                  </button>

                  {showNotifications && (
                    <div className="absolute right-0 mt-3 w-[min(20rem,calc(100vw-2rem))] rounded-[24px] border border-slate-200 bg-white/94 p-3 shadow-[0_20px_60px_rgba(15,23,42,0.14)] backdrop-blur-xl">
                      <div className="mb-2 px-2">
                        <p className="text-sm font-semibold text-slate-900">Notifications</p>
                        <p className="text-xs text-slate-500">Résumé atelier</p>
                      </div>
                      <div className="space-y-2">
                        {defaultNotifications.map((notification) => (
                          <div key={notification.id} className="rounded-2xl bg-slate-50 px-3 py-3 text-sm text-slate-600">
                            {notification.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="hidden rounded-2xl border border-slate-200 bg-white/75 px-3 py-2 shadow-sm sm:flex sm:items-center sm:gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                    <UserCircleIcon className="h-7 w-7" />
                  </div>
                  <div className="max-w-[min(11rem,34vw)]">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {user?.first_name || user?.last_name
                        ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
                        : user?.username || 'Utilisateur'}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {isPlatformSession ? 'Admin plateforme' : user?.role || 'Compte atelier'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="min-h-0 min-w-0 flex-1 pt-1 md:pt-2">
            {children}
          </main>
        </div>
      </div>
      <AppAssistant />
    </div>
  );
});
